import { useMemo, useState } from 'react';
import { AlertTriangle, Globe, Pin } from 'lucide-react';
import type { HubSource, SetDestinationsInput } from '../../lib/api';
import { useProjectTree } from '../../lib/project-tree';
import { cn, timeAgo } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';

type Save = (input: Omit<SetDestinationsInput, 'provider' | 'externalId'>) => Promise<void>;

const receiving = (s: HubSource) => s.organization || s.projects.length > 0;

/** One hub source: where it goes here, and the control that changes it. */
export function HubSourceRow({
  source,
  busy,
  onSave,
  pinTo,
}: {
  source: HubSource;
  busy: boolean;
  onSave: Save;
  /** On a project's own page: the row becomes a pin toggle for that project. */
  pinTo?: { id: string; name: string };
}) {
  const [editing, setEditing] = useState(false);
  const isCalendar = source.provider === 'GOOGLE_CALENDAR';
  const received = receiving(source);
  const projectIds = source.projects.map((p) => p.id);

  let control: React.ReactNode;
  if (source.supplied === false) {
    control = (
      <Button size="sm" disabled={busy} onClick={() => void onSave({ organization: false, projectIds: [] })}>
        Remove
      </Button>
    );
  } else if (pinTo) {
    const pinned = projectIds.includes(pinTo.id);
    control = (
      <label className="flex items-center gap-2 text-xs text-ink-secondary cursor-pointer">
        <Checkbox
          checked={pinned}
          disabled={busy}
          onChange={() =>
            void onSave({
              organization: source.organization,
              projectIds: pinned ? projectIds.filter((id) => id !== pinTo.id) : [...projectIds, pinTo.id],
              includeHistory: !received,
            })
          }
        />
        Pinned here
      </label>
    );
  } else if (isCalendar) {
    control = (
      <Button
        size="sm"
        variant={received ? 'secondary' : 'primary'}
        disabled={busy}
        onClick={() => void onSave({ organization: !received, projectIds: [], includeHistory: true })}
      >
        {received ? 'Turn off' : 'Use for automations'}
      </Button>
    );
  } else {
    control = (
      <Button size="sm" variant={received ? 'secondary' : 'primary'} disabled={busy} onClick={() => setEditing(!editing)}>
        {received ? 'Change' : 'Receive'}
      </Button>
    );
  }

  return (
    <li className={cn('px-4 py-3', source.supplied === false && 'bg-warning-bg')}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-56">
          <p className="text-sm font-medium text-ink-secondary truncate">{source.name ?? source.externalId}</p>
          {source.name && <p className="text-[11px] font-mono text-ink-faint truncate">{source.externalId}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 basis-56 flex-1">
          <Destinations source={source} />
        </div>
        <p className="text-xs text-ink-faint text-right whitespace-nowrap">{activity(source)}</p>
        <div className="flex-shrink-0">{control}</div>
      </div>

      {source.supplied === false && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-warning-text">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          The hub no longer collects this, so nothing new arrives. Remove it, or reconnect it in the hub console.
        </p>
      )}

      {editing && (
        <DestinationEditor
          source={source}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={async (input) => {
            await onSave(input);
            setEditing(false);
          }}
        />
      )}
    </li>
  );
}

function Destinations({ source }: { source: HubSource }) {
  if (source.provider === 'GOOGLE_CALENDAR') {
    return receiving(source) ? <Chip icon={Globe}>Automation triggers</Chip> : <Muted>Not received</Muted>;
  }
  if (!receiving(source)) return <Muted>Not received</Muted>;
  return (
    <>
      {source.organization && <Chip icon={Globe}>Organization</Chip>}
      {source.projects.map((p) => (
        <Chip key={p.id} icon={Pin}>
          {p.name}
        </Chip>
      ))}
    </>
  );
}

function activity(source: HubSource): string {
  if (!source.collected) return '';
  const { count, lastAt } = source.collected;
  return `${count.toLocaleString()} in the hub${lastAt ? ` · last ${timeAgo(lastAt)}` : ''}`;
}

function DestinationEditor({
  source,
  busy,
  onSave,
  onCancel,
}: {
  source: HubSource;
  busy: boolean;
  onSave: Save;
  onCancel: () => void;
}) {
  const { allProjects, organization } = useProjectTree();
  const received = receiving(source);
  const [org, setOrg] = useState(received ? source.organization : true);
  const [pinned, setPinned] = useState(new Set(source.projects.map((p) => p.id)));
  const [history, setHistory] = useState(true);
  const [search, setSearch] = useState('');

  const projects = useMemo(
    () =>
      allProjects.filter(
        (p) => p.id !== organization?.id && p.name.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [allProjects, organization?.id, search],
  );

  const toggle = (id: string) => {
    const next = new Set(pinned);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPinned(next);
  };

  const save = (organizationOn: boolean, projectIds: string[]) =>
    void onSave({ organization: organizationOn, projectIds, includeHistory: !received && history });

  return (
    <div className="mt-3 rounded-lg border border-border bg-surface-subtle p-4 space-y-4">
      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox checked={org} onChange={() => setOrg(!org)} className="mt-0.5" />
        <span className="text-sm text-ink-secondary">
          Organization
          <span className="block text-xs text-ink-muted">
            The ingestor reads each item and files it in whichever projects it belongs to.
          </span>
        </span>
      </label>

      <div className="space-y-2">
        <p className="text-sm text-ink-secondary">
          Pin to projects
          <span className="block text-xs text-ink-muted">Every item is also filed in these, whatever it is about.</span>
        </p>
        <Input placeholder="Find a project…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <ul className="max-h-48 overflow-y-auto rounded-md border border-border bg-card divide-y divide-border-subtle">
          {projects.map((p) => (
            <li key={p.id}>
              <label
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-ink-secondary cursor-pointer hover:bg-surface-subtle"
                style={{ paddingLeft: 12 + Math.max(0, p.depth - 1) * 14 }}
              >
                <Checkbox checked={pinned.has(p.id)} onChange={() => toggle(p.id)} />
                {p.name}
              </label>
            </li>
          ))}
        </ul>
      </div>

      {!received && (
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={history} onChange={() => setHistory(!history)} className="mt-0.5" />
          <span className="text-sm text-ink-secondary">
            Also file what the hub already holds
            {source.collected ? ` (${source.collected.count.toLocaleString()} items)` : ''}
            <span className="block text-xs text-ink-muted">Anything already in memory is recognised and skipped.</span>
          </span>
        </label>
      )}

      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" disabled={busy || (!org && pinned.size === 0)} onClick={() => save(org, [...pinned])}>
          {received ? 'Save' : 'Start receiving'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        {received && (
          <Button variant="ghost" size="sm" className="ml-auto text-danger-text" disabled={busy} onClick={() => save(false, [])}>
            Stop receiving
          </Button>
        )}
      </div>
    </div>
  );
}

function Chip({ icon: Icon, children }: { icon: typeof Globe; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 dark:bg-orange-500/10 px-2 py-0.5 text-xs font-medium text-orange-700 dark:text-orange-300">
      <Icon size={11} />
      {children}
    </span>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-ink-faint">{children}</span>;
}
